/**
 * Trip utility functions
 *
 * Centralized utilities for working with Trip proto messages.
 * These functions eliminate duplicated patterns across components.
 */

import type { Trip as TripMessage, Destination } from '@generated/proto/model'

/**
 * Get the primary destination for display purposes.
 *
 * Returns the first destination in the trip's destinations array.
 * Used for showing destination name, country, etc.
 *
 * Used in: PassportCard, TripListOverlay
 *
 * @example
 * ```ts
 * const destination = getPrimaryDestination(trip)
 * if (destination) {
 *   console.log(destination.name) // "Paris"
 * }
 * ```
 */
export function getPrimaryDestination(trip: TripMessage): Destination | undefined {
  return trip.destinations?.[0]
}

/**
 * Get the first destination with valid geographic coordinates.
 *
 * Returns the first destination that has both latitude and longitude defined.
 * Used for map centering, marker placement, and geographic operations.
 *
 * Used in: GlobePage, useMapMarkers
 *
 * @example
 * ```ts
 * const destination = getGeocodedDestination(trip)
 * if (destination?.location) {
 *   panTo(destination.location.latitude, destination.location.longitude)
 * }
 * ```
 */
export function getGeocodedDestination(trip: TripMessage): Destination | undefined {
  return trip.destinations?.find(
    (d) => d.location?.latitude != null && d.location?.longitude != null
  )
}

/**
 * Get geographic coordinates from a trip's first geocoded destination.
 *
 * Convenience function that extracts just the lat/lng from the first
 * destination with valid coordinates. Returns undefined if no valid
 * destination is found.
 *
 * @example
 * ```ts
 * const coords = getTripCoordinates(trip)
 * if (coords) {
 *   console.log(coords.latitude, coords.longitude)
 * }
 * ```
 */
export function getTripCoordinates(
  trip: TripMessage
): { latitude: number; longitude: number } | undefined {
  const destination = getGeocodedDestination(trip)
  if (
    destination?.location?.latitude == null ||
    destination?.location?.longitude == null
  ) {
    return undefined
  }
  return {
    latitude: destination.location.latitude,
    longitude: destination.location.longitude,
  }
}
